// src/lib/meeting-reminders.ts (CRM-4.5)
//
// Påminnelsesplan for et kundemøte (CRM-4.5). Tre ledd:
//   - day_before: e-post ved start − 24 t, flyttet inn i sendevinduet (aldri før 08:00,
//     ikke helg eller rød dag). Droppes hvis den flyttede tiden er ≥ start − 1 t.
//   - three_hours: SMS ved start − 3 t, flyttet inn i sendevinduet. Droppes hvis den
//     flyttede tiden er ≥ start − 20 min.
//   - fifteen_minutes: SMS ved start − 15 min, ALDRI flyttet (møtet er uansett i arbeidstid).
// Ledd som allerede er passert ved booking droppes, bortsett fra 15-minuttersleddet,
// som sendes straks så lenge møtet ikke har startet. Faller to ledd på samme minutt,
// beholdes bare det senere. SMS droppes når kontakten mangler mobil.
//
// Eksempel: møte 09:00 gir SMS 08:00 og 08:45.
//
// IDENTISK KOPI i src/lib/meeting-reminders.ts og supabase/functions/_shared/meeting-reminders.ts
// (bortsett fra importlinjen). Begge testes mot __tests__/meeting-reminder-vectors.json.

import { nextAllowedSendTime, type SendWindowOptions } from "./send-window";

export type ReminderKind = "day_before" | "three_hours" | "fifteen_minutes";

export interface ReminderItem {
  kind: ReminderKind;
  channel: "email" | "sms";
  sendAt: Date;
}

export interface ReminderInput {
  start: Date;
  now: Date;
  hasMobile: boolean;
  hasEmail: boolean;
}

export interface ReminderPlan {
  items: ReminderItem[];
  /** Maskinlesbare advarsler til UI og svar: no_mobile (SMS hoppes over), no_email. */
  warnings: ("no_mobile" | "no_email")[];
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const ORDER: ReminderKind[] = ["day_before", "three_hours", "fifteen_minutes"];

const minuteKey = (d: Date) => Math.floor(d.getTime() / MIN);

export function reminderPlan(input: ReminderInput, window: SendWindowOptions): ReminderPlan {
  const { start, now } = input;
  const warnings: ReminderPlan["warnings"] = [];
  if (!input.hasMobile) warnings.push("no_mobile");
  if (!input.hasEmail) warnings.push("no_email");
  if (now.getTime() >= start.getTime()) return { items: [], warnings };

  const candidates: ReminderItem[] = [];

  if (input.hasEmail) {
    const sendAt = nextAllowedSendTime(new Date(start.getTime() - 24 * HOUR), window);
    if (sendAt.getTime() < start.getTime() - HOUR && sendAt.getTime() >= now.getTime()) {
      candidates.push({ kind: "day_before", channel: "email", sendAt });
    }
  }
  if (input.hasMobile) {
    const threeHours = nextAllowedSendTime(new Date(start.getTime() - 3 * HOUR), window);
    if (threeHours.getTime() < start.getTime() - 20 * MIN && threeHours.getTime() >= now.getTime()) {
      candidates.push({ kind: "three_hours", channel: "sms", sendAt: threeHours });
    }
    const fifteen = new Date(start.getTime() - 15 * MIN);
    candidates.push({ kind: "fifteen_minutes", channel: "sms", sendAt: fifteen.getTime() < now.getTime() ? now : fifteen });
  }

  // Samme minutt: behold det senere leddet.
  const items = candidates.filter(
    (c) => !candidates.some((o) => o !== c && minuteKey(o.sendAt) === minuteKey(c.sendAt) && ORDER.indexOf(o.kind) > ORDER.indexOf(c.kind)),
  );
  items.sort((a, b) => a.sendAt.getTime() - b.sendAt.getTime());
  return { items, warnings };
}

/** Nøkkelen som gjør planlegging idempotent. Ny starttid gir nye nøkler. */
export function reminderIdempotencyKey(meetingId: string, kind: ReminderKind, start: Date): string {
  return `reminder:${meetingId}:${kind}:${Math.floor(start.getTime() / 1000)}`;
}
